import Link from 'next/link';
import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import { FileUp, Sparkles, X } from 'lucide-react';
import { btnSmall } from '@/components/svg-tools/ToolParts';
import { linkClass } from '@/components/converter/PageParts';
import { MAX_SVG_BYTES, formatBytes } from '@/utils/svg-tools/svg-source';

/**
 * The SVG code box on the tool pages: paste, type, drop an .svg file on it or open one.
 * Files are read in the browser with File.text(); nothing is uploaded.
 */
export function SvgInput({
  value,
  onChange,
  sample,
  heightClass = 'h-[220px] sm:h-[420px]',
  footer,
}: {
  value: string;
  /** fileName is set when the text came from a file or the sample. */
  onChange: (text: string, fileName?: string) => void;
  sample: { name: string; text: string };
  heightClass?: string;
  footer?: ReactNode;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<ReactNode>(null);

  async function readFile(file: File) {
    setError(null);
    const name = file.name || 'image.svg';
    if (/^image\/(png|jpe?g|webp|gif|avif|bmp)$/.test(file.type) || /\.(png|jpe?g|webp|gif|avif|bmp)$/i.test(name)) {
      setError(
        <>
          That is a bitmap image, not an SVG. To turn it into one, use the{' '}
          <Link href="/png-to-svg" className={linkClass}>PNG to SVG converter</Link>.
        </>
      );
      return;
    }
    if (file.size > MAX_SVG_BYTES) {
      setError(`That file is ${formatBytes(file.size)}. These tools take SVGs up to ${formatBytes(MAX_SVG_BYTES)}.`);
      return;
    }
    try {
      let text: string;
      if (/\.svgz$/i.test(name) && typeof DecompressionStream !== 'undefined') {
        text = await new Response(file.stream().pipeThrough(new DecompressionStream('gzip'))).text();
      } else {
        text = await file.text();
      }
      onChange(text, name);
    } catch {
      setError("Couldn't read that file.");
    }
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      readFile(file);
      return;
    }
    const text = e.dataTransfer.getData('text/plain');
    if (text) onChange(text);
  }

  return (
    <div
      className="flex min-w-0 flex-col"
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-cream-200">
        <label htmlFor="svg-code" className="text-[13px] font-semibold text-ink">
          SVG code
        </label>
        <div className="flex flex-wrap gap-1.5">
          <button type="button" className={btnSmall} onClick={() => fileRef.current?.click()}>
            <FileUp className="h-3.5 w-3.5" aria-hidden="true" />
            Open file
          </button>
          <button
            type="button"
            className={btnSmall}
            onClick={() => {
              setError(null);
              onChange(sample.text, sample.name);
            }}
          >
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            Sample
          </button>
          <button
            type="button"
            className={btnSmall}
            onClick={() => {
              setError(null);
              onChange('');
            }}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
            Clear
          </button>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".svg,.svgz,image/svg+xml"
          className="hidden"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) readFile(file);
          }}
        />
      </div>
      <div className="relative min-w-0">
        <textarea
          id="svg-code"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          autoComplete="off"
          placeholder="Paste SVG code here, or drop an .svg file"
          className={`block w-full ${heightClass} resize-y bg-white px-4 py-3 font-mono text-[12.5px] leading-[1.6] [font-variant-ligatures:none] text-ink placeholder:text-ink-faint focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky/40`}
        />
        {dragging && (
          <div className="pointer-events-none absolute inset-1 flex items-center justify-center rounded-lg border-2 border-dashed border-sky bg-sky-bg/80 text-[14px] font-medium text-sky">
            Drop the SVG to open it
          </div>
        )}
      </div>
      {error && (
        <p role="alert" className="mx-4 mb-3 rounded-md border border-cream-300 bg-cream-50 px-3 py-2 text-[13px] leading-relaxed text-clay">
          {error}
        </p>
      )}
      {footer}
    </div>
  );
}
